import React, { useState } from 'react';
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { BulkResumeService, type BulkResumeRequest } from "@/services/bulkResumeService";
import { Download, FileText, Loader2 } from "lucide-react";

interface BulkResumeData {
  headerInfo: {
    fullName: string;
    phoneNumber: string;
    email: string;
    cityST: string;
    linkedin: string;
  };
  summary: string;
  experience: Array<{
    jobTitle: string;
    company: string;
    location: string;
    startDate: string;
    endDate: string;
    bullets: string[];
  }>;
  education: Array<{
    degree: string;
    school: string;
    location: string;
    graduationDate: string;
  }>;
  skills: string[];
  resumeIdentifier?: string;
}

export default function BulkResumeGenerator() {
  const { toast } = useToast();
  const [jsonInput, setJsonInput] = useState<string>("");
  const [generating, setGenerating] = useState(false);

  const getJsonTemplate = () => {
    return JSON.stringify({
      resumes: [
        {
          resumeIdentifier: "Resume1",
          headerInfo: {
            fullName: "John Doe",
            phoneNumber: "(555) 123-4567",
            email: "john.doe@email.com",
            cityST: "New York, NY",
            linkedin: "https://linkedin.com/in/johndoe"
          },
          summary: "Experienced software engineer with 5+ years developing scalable web applications...",
          experience: [
            {
              jobTitle: "Senior Software Engineer",
              company: "Tech Company Inc",
              location: "New York, NY",
              startDate: "2020-01",
              endDate: "Present",
              bullets: [
                "Developed and maintained 5+ web applications using React and Node.js",
                "Led a team of 3 junior developers and improved code quality by 40%",
                "Implemented CI/CD pipelines reducing deployment time by 60%"
              ]
            },
            {
              jobTitle: "Software Engineer",
              company: "StartupCorp",
              location: "Boston, MA",
              startDate: "2018-06",
              endDate: "2019-12",
              bullets: [
                "Built responsive web applications using modern JavaScript frameworks",
                "Collaborated with design team to implement user-friendly interfaces"
              ]
            }
          ],
          education: [
            {
              degree: "Bachelor of Science in Computer Science",
              school: "University of Technology",
              location: "Boston, MA",
              graduationDate: "2018-05"
            }
          ],
          skills: [
            "JavaScript", "React", "Node.js", "Python", "SQL", "AWS", "Docker", "Git"
          ]
        },
        {
          resumeIdentifier: "Resume2",
          headerInfo: {
            fullName: "John Doe",
            phoneNumber: "(555) 123-4567",
            email: "john.doe@email.com",
            cityST: "New York, NY",
            linkedin: "https://linkedin.com/in/johndoe"
          },
          summary: "Results-driven software engineer specializing in full-stack development and cloud technologies...",
          experience: [
            {
              jobTitle: "Full Stack Developer",
              company: "Cloud Solutions Ltd",
              location: "San Francisco, CA",
              startDate: "2019-03",
              endDate: "Present",
              bullets: [
                "Architected microservices handling 1M+ daily requests",
                "Reduced server costs by 35% through optimization and cloud migration",
                "Mentored 2 junior developers and established best practices"
              ]
            }
          ],
          education: [
            {
              degree: "Bachelor of Science in Computer Science",
              school: "University of Technology",
              location: "Boston, MA",
              graduationDate: "2018-05"
            }
          ],
          skills: [
            "Python", "Django", "PostgreSQL", "AWS", "Kubernetes", "React", "TypeScript"
          ]
        }
      ]
    }, null, 2);
  };

  const generateBulkResumes = async () => {
    if (!jsonInput.trim()) {
      toast({
        title: "Missing JSON Data",
        description: "Please paste your JSON data to generate resumes",
        variant: "destructive"
      });
      return;
    }

    setGenerating(true);

    try {
      const parsedData = JSON.parse(jsonInput);
      
      // Support both direct resume object and resumes array
      let resumeCount = 1;
      if (parsedData.resumes && Array.isArray(parsedData.resumes)) {
        resumeCount = parsedData.resumes.length;
      } else if (parsedData.Name) {
        resumeCount = 1;
      } else {
        throw new Error("JSON must contain either a resume object with 'Name' field or a 'resumes' array");
      }

      toast({
        title: "Generating Resumes",
        description: `Processing ${resumeCount} resume(s)...`,
      });

      const response = await BulkResumeService.generateBulkResumes(parsedData);
      
      // Download each file individually with correct names
      console.log(`Downloading ${response.files.length} files...`);
      
      for (const file of response.files) {
        console.log(`Downloading: ${file.filename} (${file.size} bytes)`);
        BulkResumeService.downloadFileFromBase64(file.filename, file.data);
        
        // Small delay between downloads to prevent browser blocking
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      toast({
        title: "All Resumes Generated Successfully",
        description: `${response.totalFiles} resumes have been generated and downloaded`,
      });

    } catch (error) {
      console.error('Error generating bulk resumes:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : "Failed to parse JSON or generate resumes",
        variant: "destructive"
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="container mx-auto py-6">
      <PageHeader
        title="Bulk Resume Generator"
        description="Generate multiple resumes from JSON data using professional templates"
        backTo="/resume-builder"
      />

      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>JSON Data Input</CardTitle>
            <CardDescription>
              Paste your JSON resume data to generate multiple Word documents.
              Each resume will be created as a separate file with proper formatting.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* JSON Input */}
            <div className="space-y-2">
              <Label htmlFor="jsonInput">JSON Data</Label>
              <Textarea
                id="jsonInput"
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                rows={20}
                className="font-mono text-sm"
                placeholder="Paste your JSON resume data here..."
              />
            </div>
            
            <div className="flex gap-2">
              <Button 
                onClick={generateBulkResumes}
                disabled={generating || !jsonInput.trim()}
                className="flex-1"
                size="lg"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating Resumes...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Generate All Resumes
                  </>
                )}
              </Button>
              <Button 
                variant="outline"
                onClick={() => setJsonInput(getJsonTemplate())}
                disabled={generating}
              >
                Load Example
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Expected JSON Format</CardTitle>
            <CardDescription>
              Your JSON should contain an array of resume data objects. Each object represents one complete resume.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="text-xs bg-muted p-4 rounded border overflow-x-auto">
{`{
  "resumes": [
    {
      "resumeIdentifier": "Resume1", // Optional: helps organize output files
      "headerInfo": {
        "fullName": "Your Name",
        "phoneNumber": "(555) 123-4567",
        "email": "your.email@example.com",
        "cityST": "City, State",
        "linkedin": "LinkedIn URL"
      },
      "summary": "Professional summary paragraph...",
      "experience": [
        {
          "jobTitle": "Job Title",
          "company": "Company Name",
          "location": "City, State",
          "startDate": "2020-01",
          "endDate": "Present",
          "bullets": [
            "Achievement bullet point 1",
            "Achievement bullet point 2"
          ]
        }
      ],
      "education": [
        {
          "degree": "Degree Name",
          "school": "School Name",
          "location": "City, State",
          "graduationDate": "2020-05"
        }
      ],
      "skills": ["Skill 1", "Skill 2", "Skill 3"]
    }
    // Add more resume objects for different versions
  ]
}`}
            </pre>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>File Organization</CardTitle>
            <CardDescription>
              Generated files will be organized and named for easy identification
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 text-sm">
              <p><strong>Naming Convention:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li><code>FirstName LastName Resume 2025.docx</code></li>
                <li><code>FirstName LastName Resume - Resume1 2025.docx</code> (if resumeIdentifier provided)</li>
              </ul>
              <p className="mt-4"><strong>Features:</strong></p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Professional formatting using Docmosis templates</li>
                <li>Automatic bullet point formatting</li>
                <li>Consistent styling across all resumes</li>
                <li>Individual file downloads with correct names</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}